import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { PersonSummary } from '../../types/person';

interface PersonFilterProps {
  people: PersonSummary[];
  selectedPersonIds: string[];
  onChange: (personIds: string[]) => void;
}

const STORAGE_KEY = 'timeline_selected_people';

/**
 * PersonFilter Component
 * Multi-select filter for choosing which people to show on the timeline
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const PersonFilter: React.FC<PersonFilterProps> = ({
  people,
  selectedPersonIds,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Load saved selections from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const savedIds = JSON.parse(saved);
        if (Array.isArray(savedIds) && savedIds.length > 0) {
          onChange(savedIds);
        }
      }
    } catch (error) {
      console.warn('Failed to load saved person selections:', error);
    }
  }, []);

  // Save selections to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedPersonIds));
    } catch (error) {
      console.warn('Failed to save person selections:', error);
    }
  }, [selectedPersonIds]);

  // Filter people based on search query
  const filteredPeople = useMemo(() => {
    if (!searchQuery.trim()) return people;

    const query = searchQuery.toLowerCase();
    return people.filter(
      person =>
        person.firstName.toLowerCase().includes(query) ||
        person.lastName.toLowerCase().includes(query) ||
        (person.maidenName && person.maidenName.toLowerCase().includes(query))
    );
  }, [people, searchQuery]);

  const handleSelectAll = () => {
    onChange(people.map(p => p.id));
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  const handleTogglePerson = (personId: string) => {
    if (selectedPersonIds.includes(personId)) {
      onChange(selectedPersonIds.filter(id => id !== personId));
    } else {
      onChange([...selectedPersonIds, personId]);
    }
  };

  const handleRemoveChip = (personId: string) => {
    onChange(selectedPersonIds.filter(id => id !== personId));
  };

  // Get display names for selected people
  const selectedPeople = useMemo(() => {
    return people.filter(p => selectedPersonIds.includes(p.id));
  }, [people, selectedPersonIds]);

  return (
    <Box>
      {/* Search Input */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search people..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <Button size="small" onClick={() => setSearchQuery('')}>
                <ClearIcon fontSize="small" />
              </Button>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAll}
          disabled={selectedPersonIds.length === people.length}
        >
          Select All ({people.length})
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleDeselectAll}
          disabled={selectedPersonIds.length === 0}
        >
          Clear
        </Button>
      </Stack>

      {/* Multi-Select Dropdown */}
      <FormControl fullWidth size="small">
        <InputLabel>Select People</InputLabel>
        <Select
          multiple
          value={selectedPersonIds}
          label="Select People"
          renderValue={(selected) => `${selected.length} selected`}
          sx={{ mb: 2 }}
        >
          {filteredPeople.map((person) => (
            <MenuItem
              key={person.id}
              value={person.id}
              onClick={() => handleTogglePerson(person.id)}
            >
              <Checkbox checked={selectedPersonIds.includes(person.id)} />
              <ListItemText
                primary={`${person.firstName} ${person.lastName}`}
                secondary={
                  person.birthDate
                    ? `b. ${person.birthDate.substring(0, 4)}`
                    : undefined
                }
              />
            </MenuItem>
          ))}
          {filteredPeople.length === 0 && (
            <MenuItem disabled>
              <ListItemText primary="No people found" />
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Selected People Chips */}
      {selectedPeople.length > 0 && (
        <Box>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {selectedPeople.map((person) => (
              <Chip
                key={person.id}
                label={`${person.firstName} ${person.lastName}`}
                size="small"
                onDelete={() => handleRemoveChip(person.id)}
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PersonFilter;
